<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.shipment_notice_list.shipment_notices') }}
      </h1>
      <div
        v-if="!staticFilter.filter.asOrderGuid"
        class="ml-auto"
      >
        <v-btn
          v-if="editor && (business || user.asdb) && !staticFilter.filter.consolidator"
          to="/asn/create"
          color="primary"
          outlined
          large
          class="btn pt-3 mr-4"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          {{ $t('common.create') }}
        </v-btn>
        <v-btn
          v-if="editor && (business || user.asdb) && !staticFilter.filter.consolidator"
          color="primary"
          outlined
          large
          class="btn mr-4"
          @click="showLoadModal"
        >
          <v-icon left>
            mdi-upload
          </v-icon>
          {{ $t('common.upload') }}
        </v-btn>
        <download-excel
          v-if="staticFilter.filter.consolidator || consolidator"
          :url="`/asn/list-xlsx`"
          file-name="asn-list.xlsx"
          :params="mapParams()"
        />
        <v-btn
          outlined
          large
          @click="toggleFilter"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <shipment-notices-filter
      :is-shown="isFilterShown"
      :filter="filter"
      :plants="plants"
      @applyFilter="applyFilter"
    />
    <v-data-table
      fixed-header
      :headers="headers"
      :items="items"
      :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.number="{ item }">
        <router-link :to="`/asn/${item.id}`">
          {{ item.number }}
        </router-link>
      </template>
      <template v-slot:item.plant="{ item }">
        {{ item.plant.asdb }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ $moment(item.date).format('L') }}
      </template>
      <template v-slot:item.shippingDate="{ item }">
        {{ $moment(item.shippingDate).format('L') }}
      </template>
      <template v-slot:item.systemStatus="{ item }">
        {{ $t(`enums.asn_system_statuses.${item.systemStatus}`) }}
      </template>
      <template
        v-if="staticFilter.filter.consolidator"
        v-slot:item.trackStatus="{ item }"
      >
        {{ $t(`enums.asn_track_statuses.${item.trackStatus}`) }}
      </template>
      <template v-slot:item.print="{ item }">
        <v-tooltip
          v-if="item.actionFlags.isLabelsPrint"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="createLabels(item.id)"
              >
                mdi-label
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.print_labels') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="item.invoice.path"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <a
                :href="item.invoice.path"
                :download="item.invoice.fileName"
                class="shipment-notice-file-link"
              >
                <v-icon
                  :class="hover ? '' : 'text--disabled'"
                  color="primary"
                  size="20"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-file-document
                </v-icon>
              </a>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.print_invoice') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="item.packingList.path"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <a
                :href="item.packingList.path"
                :download="item.packingList.fileName"
                class="shipment-notice-file-link"
              >
                <v-icon
                  :class="hover ? '' : 'text--disabled'"
                  color="primary"
                  size="20"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-file-outline
                </v-icon>
              </a>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.print_packing_list') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.status="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="$router.push(`/asn/${item.id}/track-statuses`)"
              >
                mdi-car
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.track_status_history') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="$router.push(`/asn/${item.id}/customs-statuses`)"
              >
                mdi-flag
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.customs_status_history') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="$router.push(`/asn/${item.id}/system-statuses`)"
              >
                mdi-state-machine
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.system_status_history') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="item.comment"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="showAsnComment(item.comment)"
              >
                mdi-comment-eye-outline
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.comment') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip
          v-if="item.actionFlags.isEditable"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="$router.push(`/asn/${item.id}/edit`)"
              >
                mdi-pencil
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.edit') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="item.actionFlags.isShippable"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="confirmShipping(item)"
              >
                mdi-truck
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.ship') }}</span>
        </v-tooltip>
        <v-tooltip
          v-if="canBeCancelled(item)"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="showCancelAsnModal(item.id)"
              >
                mdi-cancel
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('common.cancelAsn') }}</span>
        </v-tooltip>

        <v-tooltip
          v-if="item.actionFlags.isInvoiceGeneration"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="regenerateInvoice(item.id)"
              >
                mdi-file-restore
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.regenerate_invoice') }}</span>
        </v-tooltip>

        <v-tooltip
          v-if="staticFilter.filter.consolidator"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="downloadExcel(item)"
              >
                mdi-file-excel
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.download_xls') }}</span>
        </v-tooltip>

        <v-tooltip
          v-if="item.isConsolidator && item.actionFlags.isLoaded"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-hover v-slot:default="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-bind="attrs"
                v-on="on"
                @click="$router.push(`consolidator/asn-shipment/${item.id}`)"
              >
                mdi-barcode-scan
              </v-icon>
            </v-hover>
          </template>
          <span>{{ $t('views.shipment_notice_list.edit_shipment') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <shipment-notice-load
      v-model="isLoadModalShown"
      @created="getItems"
      @hideModal="hideLoadModal"
    />
    <shipment-notice-cancel-modal
      :id="cancelAsnId"
      v-model="cancelModal"
      @confirm="cancelAsn"
    />
    <shipment-notice-errors-modal
      v-model="isErrorsModalShown"
      :error-list="errorsModalList"
      :title="$t('views.shipment_notice_list.asn_shipping_errors')"
      @hideModal="getItems"
    />
    <b-modal
      v-if="isASNCommentShow"
      ref="modal"
      v-model="isASNCommentShow"
      :title="$t('views.shipment_notice_list.comment')"
      modal-class="spso-modal"
      hide-header-close
      hide-footer
      centered
      size="lg"
    >
      <b-col>
        <div class="shipment-notice__label">
          {{ ASNComment }}
        </div>
      </b-col>
      <main class="shipment-notice__main">
        <div class="v-application d-flex mt-4">
          <v-btn
            class="mr-4 flex-grow-1"
            outlined
            large
            @click="isASNCommentShow = false"
          >
            {{ $t('common.close') }}
          </v-btn>
        </div>
      </main>
    </b-modal>
    <b-modal
      v-if="isShippingConfirmation"
      ref="modal"
      v-model="isShippingConfirmation"
      :title="$t('views.shipment_notice_list.confirm_shipping')"
      modal-class="spso-modal"
      hide-header-close
      hide-footer
      centered
      size="lg"
    >
      <main class="shipment-notice__main">
        <b-row class="v-application">
          <b-col>
            <div class="shipment-notice__label">
              {{ $t('views.shipment_notice_details.asn_number') }}
            </div>
          </b-col>
          <b-col>
            <div class="shipment-notice__value">
              {{ shippingAsn.number }}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <div class="shipment-notice__label">
              {{ $t('views.shipment_notice_details.asn_date') }}
            </div>
          </b-col>
          <b-col>
            <div class="shipment-notice__value">
              {{ $moment(shippingAsn.date).format('L LT') }}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <div class="shipment-notice__label">
              {{ $t('views.shipment_notice_details.shipto') }}
            </div>
          </b-col>
          <b-col>
            <div class="shipment-notice__value">
              {{ shippingAsn.plant.asdb }}
            </div>
          </b-col>
        </b-row>
        <div class="v-application d-flex mt-4">
          <v-btn
            class="mr-4 flex-grow-1"
            outlined
            large
            @click="isShippingConfirmation = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>

          <v-btn
            color="primary"
            class="flex-grow-1"
            outlined
            large
            @click="shipAsn(shippingAsn.id)"
          >
            {{ $t('views.shipment_notice_list.ship') }}
          </v-btn>
        </div>
      </main>
    </b-modal>
    <a
      v-if="labelPdf"
      v-show="false"
      ref="labelPdf"
      :href="labelPdf"
      download="labels.pdf"
    />
    <a
      v-if="packingListPdf"
      v-show="false"
      ref="packingListPdf"
      :href="packingListPdf"
      download="packing-lists.pdf"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import DownloadExcel from '@/components/common/DownloadExcel.vue';
import ShipmentNoticesFilter from '@/components/asn/ShipmentNoticesFilter.vue';
import ShipmentNoticeLoad from '@/components/asn/ShipmentNoticeLoad.vue';
import ShipmentNoticeErrorsModal from '@/components/asn/ShipmentNoticeErrorsModal.vue';
import ShipmentNoticeCancelModal from '@/components/asn/CancelModal.vue';

export default {
  name: 'ShipmentNoticeList',

  components: {
    DownloadExcel,
    ShipmentNoticesFilter,
    ShipmentNoticeLoad,
    ShipmentNoticeErrorsModal,
    ShipmentNoticeCancelModal,
  },

  props: {
    staticFilter: {
      type: Object,
      required: false,
      default: () => ({ enabled: false, filter: {} }),
    },
  },

  data() {
    return {
      cancelAsnId: null,
      cancelModal: false,
      headers: [
        {
          text: this.$t('common.supplier'),
          value: 'supplier.asdb',
        },
        {
          text: this.$t('common.plant'),
          value: 'plant.asdb',
        },
        {
          text: this.$t('common.number'),
          value: 'number',
        },
        {
          text: this.$t('views.shipment_notice_list.asn_date'),
          value: 'date',
        },
        {
          text: this.$t('views.shipment_notice_list.shipping_date'),
          value: 'shippingDate',
        },
        {
          text: this.$t('views.shipment_notice_list.source'),
          value: 'source',
        },
        {
          text: this.$t('views.shipment_notice_list.system_status'),
          value: 'systemStatus',
        },
        ...[this.staticFilter.filter.consolidator ? {
          text: this.$t('views.shipment_notice_list.track_status'),
          value: 'trackStatus',
        } : {}],
        {
          text: this.$t('views.shipment_notice_list.print'),
          value: 'print',
          width: 100,
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          sortable: false,
          width: 150,
        },
        {
          text: this.$t('common.actions'),
          value: 'actions',
          sortable: false,
          width: 150,
        },
      ],

      items: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['date'],
        sortDesc: [true],
      },
      total: 0,

      filter: {
        supplier: '',
        plant: '',
        startDate: '',
        endDate: '',
        number: '',
      },

      labelPdf: null,
      packingListPdf: null,

      loading: false,
      isFilterShown: false,
      isLoadModalShown: false,
      isErrorsModalShown: false,
      isShippingConfirmation: false,
      isASNCommentShow: false,
      ASNComment: '',
      shippingAsn: null,
      errorsModalList: [],
      clickProcessing: false,
      clickCount: 0,
    };
  },

  computed: {
    ...mapState({
      user: 'user',
      supplierData: 'supplier',
    }),

    ...mapGetters(['business', 'full', 'editor', 'consolidator']),

    plants() {
      return this.supplierData ? this.supplierData.plants : [];
    },
  },

  watch: {
    options: {
      handler() {
        if (!this.loading) {
          this.getItems();
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.init();
  },

  activated() {
    this.getItems();
  },

  methods: {
    init() {
      if (this.staticFilter.enabled) {
        this.filter = {
          ...this.filter,
          ...this.staticFilter.filter,
        };
        return;
      }

      if (!this.full) {
        this.filter.supplier = this.user.asdb;
      }

      if (this.supplierData) {
        const [plantData] = this.supplierData.plants;
        if (plantData) {
          this.filter.plant = plantData.plant;
        }
      }
    },

    mapParams() {
      const {
        sortBy, sortDesc,
      } = this.options;

      const filter = this.mapFilter();

      const params = {};
      params.query = filter;

      if (sortBy && sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }

      return params;
    },

    async getItems() {
      const { page, itemsPerPage } = this.options;

      const params = this.mapParams();

      params.pageSize = itemsPerPage;
      params.page = page;

      this.loading = true;

      try {
        const { data = {} } = await this.$http.get('/asn', { params });
        this.items = (data.rows || []).map((item) => ({
          ...item,
          date: new Date(item.date),
          shippingDate: new Date(item.shippingDate),
          estimatedDate: new Date(item.estimatedDate),
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
          invoice: { ...item.invoice, path: '' },
          packingList: { ...item.packingList, path: '' },
        }));
        this.total = data.total;
        const fileLinks = [];

        this.items.forEach((item) => {
          fileLinks.push(
            ...this.setFilePath(item.invoice, this.$config.INVOICES_CONTAINER),
            ...this.setFilePath(item.packingList, this.$config.PACKING_LIST_CONTAINER),
          );

          const isNotCanceled = this.checkSystemStatus(
            item, ['ASN_CANCELED'], true,
          );

          const isLoaded = this.checkSystemStatus(
            item, ['ASN_LOADED_TO_MBS_WITH_ERRORS', 'ASN_LOADED_TO_MBS_NO_ERRORS'], false,
          );

          const isLoadedWithoutErrors = this.checkSystemStatus(
            item, ['ASN_LOADED_TO_MBS_NO_ERRORS'], false,
          );

          item.actionFlags = {
            isLabelsPrint: isNotCanceled,
            isEditable: isLoaded,
            isShippable: isLoadedWithoutErrors,
            isInvoiceGeneration: isLoadedWithoutErrors && item.import,
          };
        });
        this.loadFilePaths(fileLinks);
        this.$emit('asnLoaded', this.items);
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
      }
    },

    checkSystemStatus(item, statusList, invert = false) {
      const res = statusList.includes(item.systemStatus);
      return invert ? !res : res;
    },

    isShippableAsn(item, type) {
      if (!item || type !== 'row') return '';
      if (item.actionFlags.isShippable) return 'table-success';

      return '';
    },

    setFilePath(obj, container) {
      if (!obj.fileId) {
        return [];
      }

      return [{
        ref: obj,
        params: {
          container, file: obj.fileId, operation: 'read', downloadName: obj.fileName,
        },
      }];
    },

    async loadFilePaths(files) {
      try {
        if (files.length) { // Do not make "empty requests"
          const { data = {} } = await this.$http.post('/containers/list-path',
            { files: files.map((file) => file.params) });

          const { paths = [] } = data;

          paths.filter((filterPath) => !filterPath.isError).forEach((path) => {
            const obj = files.find((file) => {
              const props = ['container', 'file', 'operation', 'downloadName'];
              return props.every((prop) => (file.params[prop] === path.file[prop]));
            });

            if (obj) {
              obj.ref.path = path.value.path;
            }
          });
        }
      } catch (err) {
        this.$http.mockApiError(err);
      }
    },

    toggleFilter() {
      this.isFilterShown = !this.isFilterShown;
    },

    applyFilter(filter) {
      this.filter = { ...filter };
      this.options.page = 1;
      this.getItems();
    },

    mapFilter() {
      const filter = {};
      if (this.filter.supplier) {
        filter['supplier.asdb'] = this.filter.supplier;
      }
      if (this.filter.plant) {
        filter['plant.asdb'] = this.filter.plant;
      }
      if (this.filter.consolidator) { // Filter ASN with consolidator destination
        filter['plant.asdb'] = this.filter.consolidator;
      }

      if (this.filter.number) {
        filter.number = this.filter.number;
      }

      if (this.filter.isAfterSales) {
        filter.isAfterSales = this.filter.isAfterSales;
      }

      if (this.filter.asOrderGuid) {
        filter.asOrderGuid = this.filter.asOrderGuid;
      }

      if (this.filter.startDate || this.filter.endDate) {
        filter.shippingDate = {};
      }
      if (this.filter.startDate) {
        filter.shippingDate.$gte = this.$moment(this.filter.startDate);
      }
      if (this.filter.endDate) {
        filter.shippingDate.$lte = this.$moment(this.filter.endDate).endOf('day');
      }

      if (this.filter.systemStatus) {
        filter.systemStatus = this.filter.systemStatus;
      }

      if (this.filter.trackStatus) {
        filter.trackStatus = this.filter.trackStatus;
      }

      return filter;
    },

    showLoadModal() {
      this.isLoadModalShown = true;
    },

    hideLoadModal() {
      this.isLoadModalShown = false;
    },

    async createLabels(id) {
      try {
        this.clickProcessing = true;
        await Promise.all([
          this.getLabels(id),
          this.getPackingLists(id),
        ]);
      } finally {
        setTimeout(this.enableLinks, 3000);
      }
    },

    async getLabels(id) {
      try {
        const { data } = await this.$http.get(`/labels/${id}?type=label`, {
          responseType: 'blob',
        });
        if (data) {
          this.labelPdf = URL.createObjectURL(data);
          await this.$nextTick();
          this.$refs.labelPdf.click();
        }
      } catch (err) {
        this.$http.mockApiError(err);
      }
    },

    async getPackingLists(id) {
      try {
        const { data, status } = await this.$http.get(`/labels/${id}?type=packingList&noempty=false`, {
          responseType: 'blob',
        });
        if (status === 204) {
          this.$toast(this.$t('views.shipment_notice_list.no_packing_list_data'), {
            title: this.$t('toast.warning'),
            variant: 'warning',
          });
          return;
        }
        if (data) {
          this.packingListPdf = URL.createObjectURL(data);
          await this.$nextTick();
          this.$refs.packingListPdf.click();
        }
      } catch (err) {
        const { response = {} } = err;
        const { data: responseData = {} } = response;
        if (response.status === 400 && responseData.type === 'application/json') {
          try {
            const responseBody = await responseData.text();
            const errorBody = JSON.parse(responseBody);
            if (errorBody.type === 'NO_PACKING_LIST_DATA') {
              this.$toast(this.$t('views.shipment_notice_list.no_packing_list_data'), {
                title: this.$t('toast.warning'),
                variant: 'warning',
              });
            }
          } catch (parseError) {
            this.$http.mockApiError(parseError);
          }
        } else {
          this.$http.mockApiError(err);
        }
      }
    },

    canBeCancelled(item) {
      const cancelAsnStatuses = ['ASN_LOADED_TO_MBS_NO_ERRORS', 'ASN_LOADED_TO_MBS_WITH_ERRORS'];
      return cancelAsnStatuses.includes(item.systemStatus);
    },

    showAsnComment(comment) {
      this.ASNComment = comment;
      this.isASNCommentShow = true;
    },

    confirmShipping(asn) {
      this.shippingAsn = asn;
      this.isShippingConfirmation = true;
    },

    async shipAsn(id) {
      this.isShippingConfirmation = false;

      if (!this.checkLinksEnabled()) return;

      try {
        this.clickProcessing = true;
        this.clickCount += 1;
        await this.$http.patch(`/asn/${id}/ship-asn`);
        this.$toast(this.$t('views.shipment_notice_list.asn_shipping_req_sent'));
        await this.getItems();
      } catch (err) {
        // Show errors dialog
        if (err.translatedErrorList) {
          this.errorsModalList = err.translatedErrorList;
          this.isErrorsModalShown = true;
        } else {
          this.$toast(this.$t('views.shipment_notice_list.asn_shipping_req_error'), {
            title: this.$t('toast.danger'),
            variant: 'danger',
          });
        }
      } finally {
        setTimeout(this.enableLinks, 3000);
      }
    },

    showCancelAsnModal(id) {
      this.cancelAsnId = id;
      this.cancelModal = true;
    },

    async cancelAsn(id) {
      this.isShippingConfirmation = false;

      if (!this.checkLinksEnabled()) return;

      try {
        this.clickProcessing = true;
        this.clickCount += 1;
        await this.$http.patch(`/asn/${id}/cancel-draft`);
        this.$toast(this.$t('views.shipment_notice_list.asn_cancelling_req_sent'));
        await this.getItems();
      } catch (err) {
        // Show errors dialog
        if (err.translatedErrorList) {
          this.errorsModalList = err.translatedErrorList;
          this.isErrorsModalShown = true;
        } else {
          this.$toast(this.$t('views.shipment_notice_list.asn_cancel_req_error'), {
            title: this.$t('toast.danger'),
            variant: 'danger',
          });
        }
      } finally {
        setTimeout(this.enableLinks, 3000);
      }
    },

    async regenerateInvoice(id) {
      if (!this.checkLinksEnabled()) return;

      try {
        this.clickProcessing = true;

        await this.$http.patch(`/asn/${id}/regenerate-invoice`);
        this.$toast(this.$t('views.shipment_notice_list.invoice_regeneration_req_sent'));
        await this.getItems();
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        setTimeout(this.enableLinks, 3000);
      }
    },

    checkLinksEnabled() {
      if (this.clickProcessing) {
        this.$toast(this.$t('views.shipment_notice_list.too_often_clicks'), {
          title: this.$t('toast.warning'),
          variant: 'warning',
        });
        return false;
      }

      return true;
    },

    enableLinks() {
      this.clickCount -= 1;
      this.clickProcessing = false;
    },

    async downloadExcel(asn) {
      const fileName = `asn-${asn.supplier.asdb}-${asn.id}.xlsx`;

      try {
        const { data } = await this.$http.get(`/asn/${asn.id}/xlsx`, { responseType: 'blob' });

        if (data) {
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        }
      } catch (err) {
        this.$http.mockApiError(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shipment-notice-file {
  &-link {
    text-decoration: none;
    transition: color 0.3s ease-in-out 0s;
    margin-right: 8px !important;

    &:hover {
      color: var(--margaritas);
    }
  }
}

.shipment-notice__main {
  padding: 25px;
  background-color: #fff;

  .shipment-notice__label {
    font-size: 20px;
    font-weight: 500;
    color: var(--topaz);
  }

  .shipment-notice__value {
    font-size: 18px;
    color: var(--topaz);
    min-height: 27px;
  }
}
</style>
